import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, Phone, MapPin, Send, Check, Loader2, AlertCircle } from "lucide-react";

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle, loading, success, error

  const handleChange = (e) => {
    setFormState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      // Create form data for Netlify Forms submission
      const formData = new URLSearchParams();
      formData.append("form-name", "contact");
      formData.append("name", formState.name);
      formData.append("email", formState.email);
      formData.append("subject", formState.subject);
      formData.append("message", formState.message);

      const response = await fetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setStatus("success");
        setFormState({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setStatus("idle"), 5000);
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setTimeout(() => setStatus("idle"), 5000);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "hello@pureintentional.com",
      href: "mailto:hello@pureintentional.com",
    },
    {
      icon: Phone,
      label: "Phone",
      value: "(203) 243-9151",
      href: "tel:+12032439151",
    },
    {
      icon: MapPin,
      label: "Location",
      value: "United States",
      href: null,
    },
  ];

  return (
    <section
      ref={ref}
      id="contact"
      className="py-24 md:py-32 bg-brand-softBeige relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-warmGold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-deepGreen/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-brand-warmGold text-xs tracking-[0.3em] uppercase font-body block mb-4"
          >
            Get in Touch
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl font-light text-brand-deepGreen mb-4"
          >
            Contact Us
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-divider mx-auto mb-6"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-brand-coconutBrown/70 font-body font-light max-w-xl mx-auto"
          >
            Have a question or want to learn more about our products? We'd love to hear from you.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-2 space-y-8"
          >
            <div>
              <h3 className="font-heading text-2xl font-light text-brand-deepGreen mb-6">
                Let's Connect
              </h3>
              <p className="text-brand-coconutBrown/70 font-body font-light leading-relaxed">
                Whether you have questions about our herbal hair care products, need recommendations for your hair type, or just want to say hello — we're here for you.
              </p>
            </div>

            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-deepGreen/5 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-brand-deepGreen" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-brand-coconutBrown/50 text-xs tracking-wider uppercase font-body block mb-1">
                      {item.label}
                    </span>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-brand-deepGreen font-body font-medium hover:text-brand-warmGold transition-colors"
                        data-testid={`contact-${item.label.toLowerCase()}`}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-brand-deepGreen font-body font-medium">
                        {item.value}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact Form - Netlify Forms Compatible */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="lg:col-span-3"
          >
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 md:p-10 shadow-sm"
              data-testid="contact-form"
            >
              {/* Hidden field for Netlify form name */}
              <input type="hidden" name="form-name" value="contact" />
              
              {/* Honeypot field for spam protection */}
              <p className="hidden">
                <label>
                  Don't fill this out if you're human: 
                  <input name="bot-field" />
                </label>
              </p>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="name"
                    className="text-brand-coconutBrown/70 text-xs tracking-wider uppercase font-body block mb-2"
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-softBeige/50 border border-brand-deepGreen/10 rounded-xl px-5 py-4 text-brand-deepGreen font-body text-sm focus:outline-none focus:border-brand-warmGold focus:ring-2 focus:ring-brand-warmGold/20 transition-all"
                    placeholder="Jane Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="text-brand-coconutBrown/70 text-xs tracking-wider uppercase font-body block mb-2"
                  >
                    Your Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-softBeige/50 border border-brand-deepGreen/10 rounded-xl px-5 py-4 text-brand-deepGreen font-body text-sm focus:outline-none focus:border-brand-warmGold focus:ring-2 focus:ring-brand-warmGold/20 transition-all"
                    placeholder="jane@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="subject"
                  className="text-brand-coconutBrown/70 text-xs tracking-wider uppercase font-body block mb-2"
                >
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formState.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-brand-softBeige/50 border border-brand-deepGreen/10 rounded-xl px-5 py-4 text-brand-deepGreen font-body text-sm focus:outline-none focus:border-brand-warmGold focus:ring-2 focus:ring-brand-warmGold/20 transition-all"
                  placeholder="How can we help?"
                  data-testid="contact-subject-input"
                />
              </div>

              <div className="mb-8">
                <label
                  htmlFor="message"
                  className="text-brand-coconutBrown/70 text-xs tracking-wider uppercase font-body block mb-2"
                >
                  Your Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-brand-softBeige/50 border border-brand-deepGreen/10 rounded-xl px-5 py-4 text-brand-deepGreen font-body text-sm focus:outline-none focus:border-brand-warmGold focus:ring-2 focus:ring-brand-warmGold/20 transition-all resize-none"
                  placeholder="Tell us more about your inquiry..."
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className={`w-full flex items-center justify-center gap-3 py-4 px-8 rounded-full font-body font-semibold text-sm tracking-wider uppercase transition-all duration-500 ${
                  status === "success"
                    ? "bg-green-600 text-white"
                    : status === "error"
                    ? "bg-red-500 text-white"
                    : "bg-brand-deepGreen text-white hover:bg-brand-deepGreen/90 hover:shadow-lg hover:shadow-brand-deepGreen/20"
                }`}
                data-testid="contact-submit-btn"
              >
                {status === "loading" && (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                )}
                {status === "success" && (
                  <>
                    <Check className="w-5 h-5" />
                    Message Sent Successfully!
                  </>
                )}
                {status === "error" && (
                  <>
                    <AlertCircle className="w-5 h-5" />
                    Error - Please Try Again
                  </>
                )}
                {status === "idle" && (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
